// src/api/events.js
import { db } from "../firebase";
import { ref, push, set, onValue, update, remove } from "firebase/database";

/* ------------------------------------------------------------------
   🔸 RTDB (EMP/Admin UI copy) — existing helpers
------------------------------------------------------------------- */

/** Realtime listener for all events (object keyed by id). */
export function watchEvents(cb) {
  return onValue(ref(db, "/events"), (snap) => cb(snap.val() || {}));
}

/** Create a STANDARD questionnaire (non–peer-review). */
export async function createQuestionnaire(payload = {}) {
  const idRef = push(ref(db, "/events"));
  const id = idRef.key;
  const now = Date.now();

  const data = {
    id,
    type: "QUESTIONNAIRE",
    status: payload.status || "DRAFT",
    title: payload.title || "",
    description: payload.description || "",
    instructions: payload.instructions || "",
    // { start, end, entryAllowedAt, durationMinutes }
    dates: payload.dates || null,
    audience: payload.audience || { all: true },
    questionnaire: payload.questionnaire || { questions: [] },
    questionnaireMode: "STANDARD",
    version: 1,
    createdAt: now,
    updatedAt: now,
  };

  await set(idRef, data);
  return id;
}

/** Create a PEER-REVIEW questionnaire (Performer of the Month). */
export async function createPeerReviewQuestionnaire(payload = {}) {
  const idRef = push(ref(db, "/events"));
  const id = idRef.key;
  const now = Date.now();

  const includeSelf = payload?.peerReview?.includeSelf ?? true;
  const schema =
    payload?.peerReview?.schema ||
    payload?.questionnaire?.questions ||
    [];

  const data = {
    id,
    type: "QUESTIONNAIRE",
    status: payload.status || "DRAFT",
    title: payload.title || "Performer of the Month",
    description: payload.description || "Peer review questionnaire",
    instructions: payload.instructions || "",
    dates: payload.dates || null,
    audience: payload.audience || { all: true },

    // legacy (kept for older clients)
    peerReview: {
      enabled: true,
      includeSelf,
      targets: payload?.peerReview?.targets || [],
      schema,
    },

    // normalized (for newer clients)
    questionnaire: {
      mode: "PEER_REVIEW",
      includeSelf,
      questions: schema,
    },
    questionnaireMode: "PEER_REVIEW",

    version: 1,
    createdAt: now,
    updatedAt: now,
  };

  await set(idRef, data);
  return id;
}

/** Create a GAME event (shown in EMP/Admin UI via RTDB). */
export async function createGame(payload = {}) {
  const idRef = push(ref(db, "/events"));
  const id = idRef.key;
  const now = Date.now();

  const data = {
    id,
    type: "GAME",
    status: payload.status || "DRAFT",
    title: payload.title || "",
    description: payload.description || "",
    // { start, end } or null
    dates: payload.dates || null,
    audience: payload.audience || { all: true },
    game: {
      url: payload?.game?.url || "",
      attemptLimit: Number(payload?.game?.attemptLimit ?? 3),
      tokenDurationMinutes: Number(payload?.game?.tokenDurationMinutes ?? 15),
    },
    version: 1,
    createdAt: now,
    updatedAt: now,
  };

  await set(idRef, data);
  return id;
}

/** Publish / Unpublish / Update / Delete in RTDB (EMP/Admin UI copy). */
export async function publishEvent(id) {
  await update(ref(db, `/events/${id}`), {
    status: "PUBLISHED",
    updatedAt: Date.now(),
  });
}
export async function unpublishEvent(id) {
  await update(ref(db, `/events/${id}`), {
    status: "DRAFT",
    updatedAt: Date.now(),
  });
}
export async function updateEvent(id, patch = {}) {
  if (!id) throw new Error("Missing event id");
  await update(ref(db, `/events/${id}`), { ...patch, updatedAt: Date.now() });
}
export async function deleteEvent(id) {
  if (!id) throw new Error("Missing event id");
  await remove(ref(db, `/events/${id}`));
}

/* ------------------------------------------------------------------
   🔥 ADMIN → CORE (Cloud Functions)
------------------------------------------------------------------- */

const CORE_API_RAW =
  import.meta.env.VITE_CORE_API ||
  "https://us-central1-tdo-core.cloudfunctions.net/api";

/** 🔧 Joiner: safely combines base + path (avoids double /api/api) */
function joinCore(path) {
  const base = CORE_API_RAW.replace(/\/+$/, ""); // remove trailing slash
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

/** Auth-aware fetch to Core (adds Firebase ID token if available). */
async function coreAuthFetch(path, opts = {}) {
  let idToken = null;
  try {
    const { auth } = await import("../firebase");
    idToken = await auth.currentUser?.getIdToken?.();
  } catch (_) {}

  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;

  const res = await fetch(joinCore(path), { ...opts, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Core API ${res.status}: ${text}`);
  }
  return res;
}

/** GET admin games list (Core) */
export async function listAdminGames({ status = "all" } = {}) {
  const r = await coreAuthFetch(`/api/admin/games?status=${encodeURIComponent(status)}`);
  return r.json();
}

/** POST create admin game (Core) */
export async function createAdminGame(payload) {
  const r = await coreAuthFetch(`/api/admin/games`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return r.json();
}

/** POST publish game (Core) */
export async function publishAdminGame(eventId) {
  const r = await coreAuthFetch(`/api/admin/games/${eventId}/publish`, { method: "POST" });
  return r.json();
}

/** POST unpublish game (Core) */
export async function unpublishAdminGame(eventId) {
  const r = await coreAuthFetch(`/api/admin/games/${eventId}/unpublish`, { method: "POST" });
  return r.json();
}

/** DELETE game (Core) */
export async function deleteAdminGame(eventId) {
  const r = await coreAuthFetch(`/api/admin/games/${eventId}`, { method: "DELETE" });
  return r.json();
}

/** POST preview launch URL (Core) */
export async function previewLaunchUrl(eventId) {
  const r = await coreAuthFetch(`/api/admin/games/${eventId}/preview-url`, { method: "POST" });
  return r.json();
}

/* ------------------------------------------------------------------
   🪞 RTDB Mirror (Core → EMP) — UPDATED to be more robust
------------------------------------------------------------------- */

const toMs = (v) =>
  typeof v === "number" ? v : v ? new Date(v).getTime() : null;

function minutesBetween(startAt, endAt) {
  const s = toMs(startAt);
  const e = toMs(endAt);
  if (!s || !e) return null;
  return Math.max(1, Math.round((e - s) / 60000));
}

/**
 * Upsert RTDB /events/{id} with a minimal GAME event shape from Core.
 * Accepts ISO strings or ms numbers for dates.
 * Also stores attemptLimit + tokenDurationMinutes inside game.
 *
 * coreGame {
 *   id, title, description,
 *   startAt, endAt, entryAllowedAt, durationMinutes,
 *   audience, points, gameUrl, status,
 *   attemptsPerUser, tokenDurationMinutes
 * }
 */
export async function mirrorCoreGameToRTDB(coreGame) {
  if (!coreGame?.id) throw new Error("mirrorCoreGameToRTDB: missing coreGame.id");

  const {
    id,
    title = "Game",
    description = "",
    startAt = null,
    endAt = null,
    entryAllowedAt = startAt ?? null,
    durationMinutes = minutesBetween(startAt, endAt),
    audience = { all: true },
    points = 0,
    gameUrl = "",
    status = "PUBLISHED",
    attemptsPerUser = 3,
    tokenDurationMinutes = 15,
  } = coreGame;

  const now = Date.now();
  const data = {
    id,
    type: "GAME",
    status,
    title,
    description,
    dates: {
      start: toMs(startAt),
      end: toMs(endAt),
      entryAllowedAt: toMs(entryAllowedAt),
      durationMinutes,
    },
    audience,
    points,
    game: {
      url: gameUrl,
      attemptLimit: Number(attemptsPerUser ?? 3),
      tokenDurationMinutes: Number(tokenDurationMinutes ?? 15),
    },
    version: 1,
    createdAt: now,
    updatedAt: now,
  };

  await set(ref(db, `/events/${id}`), data);
}

/** Flip only the status in RTDB when you unpublish on Core. */
export async function mirrorCoreGameStatus(id, status = "DRAFT") {
  if (!id) throw new Error("mirrorCoreGameStatus: missing id");
  await update(ref(db, `/events/${id}`), {
    status,
    updatedAt: Date.now(),
  });
}

/** Remove the mirrored node on delete. */
export async function deleteMirroredCoreGame(id) {
  if (!id) throw new Error("deleteMirroredCoreGame: missing id");
  await remove(ref(db, `/events/${id}`));
}
